import { useQuery } from '@tanstack/react-query'
import { getHeaders } from 'services/api/utils'
import { getDateWithClientTimezone } from 'utils/date'
import { findOutWhichEnv, getEnvPath } from 'utils/env'

const env = findOutWhichEnv()
const BASE_URL = `${getEnvPath(env)}/hsmservices/api`

/**
 * Fetches a list of export history records based on provided filters.
 *
 * @async
 * @function getExportHistoryList
 * @param {Object} filters - The filters to apply to the export history query.
 * @param {string}   filters.endDate - The end date for filtering records.
 * @param {string}   filters.startDate - The start date for filtering records.
 * @param {string[]} [filters.status] - Array of status values to filter by.
 * @param {string}   [filters.startExportPeriod] - The start of the export period.
 * @param {string}   [filters.endExportPeriod] - The end of the export period.
 * @param {number}   filters.page - The page number for pagination.
 * @param {number}   filters.pageSize - The number of records per page.
 * @param {number}   filters.tenantId - The tenant ID for the messages.
 * @param {string}   filters.recipientEmail - The E-mail that will receive the link to download.
 * @returns {Promise<Object>} The response object containing export history data or an error object.
 */
async function getExportHistoryList(filters) {
  try {
    const { status, endDate, startDate, startExportPeriod, endExportPeriod, page, pageSize, tenantId } = filters

    const params = new URLSearchParams()
    /**
     * typeList is default '0' because currently
     * just possible one type of export history.
     * (backend builded it this way)
     */
    params.append('typeList', 0)
    if (tenantId) params.append('tenantId', tenantId)
    if (status?.length) {
      status.forEach((item) => {
        params.append('statusList', item)
      })
    }
    if (startDate) params.append('requestDateStart', startDate)
    if (endDate) params.append('requestDateEnd', endDate)
    if (page) params.append('page', page)
    if (pageSize) params.append('pageSize', pageSize)
    if (startExportPeriod && endExportPeriod) {
      const dynamicFilter = [
        {
          FilterName: 'startDate',
          FilterValue: getDateWithClientTimezone(new Date(startExportPeriod)).clientWithTimezone,
          Operator: 3,
          ValueType: 1,
        },
        {
          FilterName: 'endDate',
          FilterValue: getDateWithClientTimezone(new Date(endExportPeriod)).clientWithTimezone,
          Operator: 4,
          ValueType: 1,
        },
      ]

      // Filtros no formato esperado pelo backend
      dynamicFilter.forEach((filter, index) => {
        Object.entries(filter).forEach(([key, value]) => {
          params.append(`dynamicFilters[${index}].${key}`, value)
        })
      })
    }

    const queryString = params.toString()

    let url = `${BASE_URL}/reportexport`

    if (queryString) {
      url = `${BASE_URL}/reportexport?${queryString}`
    }

    const response = await fetch(url, getHeaders('GET'))

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching data:', error)

    return {
      data: [],
      error: true,
      message: 'No data found',
      status: 'error',
    }
  }
}

export const QUERY_EXPORT_HISTORY_KEY = 'get-export-history-list'

const useGetExportHistoryList = ({
  status,
  endDate,
  startDate,
  startExportPeriod,
  endExportPeriod,
  page,
  pageSize,
  tenantId,
}) => {
  return useQuery({
    queryKey: [
      QUERY_EXPORT_HISTORY_KEY,
      { status, endDate, startDate, startExportPeriod, endExportPeriod, page, pageSize, tenantId },
    ],
    queryFn: () =>
      getExportHistoryList({
        status,
        endDate,
        startDate,
        startExportPeriod,
        endExportPeriod,
        page,
        pageSize,
        tenantId,
      }),
    select: (data) => data,
    retry: 1,
    keepPreviousData: true,
    staleTime: 2 * (60 * 1000), // 2 mins
    cacheTime: 4 * (60 * 1000), // 4 mins
  })
}

export default useGetExportHistoryList
